/* ==========================================================================
   ORBELLIQ® v4.0 UNIFIED MASTER ENGINE (orbelliq.in)
   Zero-conflict architecture combining:
   - Intro Preloader, Top Progress Bar, Circular Back-to-Top Ring & Header Auto-Hide
   - Mobile Navigation, Predictive Search Overlay & Product Gallery Lightbox
   - Shopify Markets localization and native Shopify cart drawer
   - Interactive product discovery, bundle selection and compression illustration
   - Quick-view, comparison, product quiz and scroll interactions
   - Scroll-Reveal IntersectionObserver & 3D Magnetic Pointer Physics
   ========================================================================== */
(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  let config = {};
  try { config = JSON.parse(document.getElementById('oq-config')?.textContent || '{}'); } catch (e) { config = {}; }
  const routes = config.routes || {};
  const text = config.strings || {};
  const SEARCH_SECTION = config.searchSection || 'orbelliq-predictive-search';
  const rootUrl = routes.root || '/';

  /* ---------- 1. Toast Notification ------------------------------------- */
  let toastTimer;
  const showToast = (message) => {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3800);
  };

  /* ---------- 2. Scroll Lock & Focus Trap ------------------------------- */
  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; root.classList.add('oq-lock'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) root.classList.remove('oq-lock'); };
  const focusables = (scope) => $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', scope).filter((el) => el.offsetParent !== null || el === document.activeElement);
  const trapTab = (event, scope) => {
    if (event.key !== 'Tab') return;
    const items = focusables(scope);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  /* ---------- 3. Intro Preloader ---------------------------------------- */
  const intro = $('#oq-intro');
  if (intro) {
    const finish = () => { intro.remove(); root.classList.remove('oq-intro'); };
    window.setTimeout(finish, 2400);
  }

  /* ---------- 4. Header / Progress / Back-to-Top / Parallax ------------- */
  const headerGroup = $('.shopify-section-group-header-group');
  const announcement = $('.announcement');
  const progressBar = $('[data-progress]');
  const topButton = $('[data-top]');
  const topRing = $('[data-top] .bar');
  let lastY = window.scrollY;
  let ticking = false;
  let menuOpen = false;
  let searchOpen = false;

  const setAnnouncementOffset = () => {
    root.style.setProperty('--ann-h', `${announcement ? announcement.offsetHeight : 0}px`);
  };

  const onScroll = () => {
    const y = window.scrollY;
    const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const p = clamp(y / max, 0, 1);
    root.classList.toggle('oq-scrolled', y > 40);
    if (progressBar) progressBar.style.transform = `scaleX(${p})`;
    if (topButton) {
      topButton.classList.toggle('is-visible', y > 700);
      if (topRing) topRing.style.strokeDashoffset = String(132 * (1 - p));
    }
    if (headerGroup && !reduced) {
      const hide = y > 360 && y > lastY + 4 && !menuOpen && !searchOpen;
      const show = y < lastY - 4 || y <= 360;
      if (hide) root.classList.add('oq-header-hidden');
      else if (show) root.classList.remove('oq-header-hidden');
    }
    const heroMedia = $('.hero-media');
    if (heroMedia && !reduced && y < window.innerHeight * 1.3) heroMedia.style.transform = `translate3d(0,${(y * 0.15).toFixed(1)}px,0)`;
    const mark = $('.footer-mark');
    if (mark) {
      const rect = mark.getBoundingClientRect();
      const t = clamp((window.innerHeight - rect.top) / (window.innerHeight + rect.height), 0, 1);
      mark.style.setProperty('--fm', `${(100 - t * 130).toFixed(1)}%`);
    }
    lastY = y;
    ticking = false;
  };
  const requestScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(onScroll); } };
  window.addEventListener('scroll', requestScroll, { passive: true });
  window.addEventListener('resize', () => { setAnnouncementOffset(); requestScroll(); }, { passive: true });
  setAnnouncementOffset();
  onScroll();
  topButton?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' }));

  /* ---------- 5. Single Unified Mobile Navigation ----------------------- */
  const menuButton = $('[data-menu-toggle]');
  const mobileNav = $('[data-mobile-nav]');
  const setMenu = (open) => {
    if (!menuButton || !mobileNav) return;
    menuOpen = open;
    menuButton.setAttribute('aria-expanded', String(open));
    menuButton.setAttribute('aria-label', open ? (text.closeMenu || 'Close menu') : (text.openMenu || 'Open menu'));
    mobileNav.classList.toggle('is-open', open);
    if (open) {
      root.classList.remove('oq-header-hidden');
      $$(':scope > a, :scope > details > summary', mobileNav).forEach((el, i) => el.style.setProperty('--i', i));
    }
  };
  menuButton?.addEventListener('click', () => setMenu(menuButton.getAttribute('aria-expanded') !== 'true'));
  mobileNav?.addEventListener('click', (event) => { if (event.target.closest('a')) setMenu(false); });
  window.matchMedia('(min-width: 901px)').addEventListener?.('change', (event) => { if (event.matches) setMenu(false); });

  /* ---------- 6. Predictive Search Overlay ------------------------------ */
  const search = $('[data-search]');
  const searchInput = $('[data-search-input]', search || document);
  const searchResults = $('[data-search-results]', search || document);
  const searchHint = $('[data-search-hint]', search || document);
  let searchReturn = null;
  let searchTimer;
  let searchAbort;
  const setSearch = (open) => {
    if (!search || searchOpen === open) return;
    searchOpen = open;
    search.classList.toggle('is-open', open);
    search.setAttribute('aria-hidden', String(!open));
    if (open) {
      searchReturn = document.activeElement;
      root.classList.remove('oq-header-hidden');
      lockScroll();
      window.setTimeout(() => searchInput?.focus(), 300);
    } else {
      unlockScroll();
      searchReturn?.focus?.();
    }
  };
  $$('[data-search-open]').forEach((el) => el.addEventListener('click', (event) => { if (!search) return; event.preventDefault(); setSearch(true); }));
  $$('[data-search-close]').forEach((el) => el.addEventListener('click', () => setSearch(false)));
  search?.addEventListener('keydown', (event) => trapTab(event, search));
  const runSearch = async (query) => {
    if (!searchResults) return;
    if (query.length < 2) { searchResults.replaceChildren(); if (searchHint) searchHint.hidden = false; return; }
    if (searchHint) searchHint.hidden = true;
    searchAbort?.abort();
    searchAbort = new AbortController();
    try {
      const url = `${routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last&section_id=${SEARCH_SECTION}`;
      const response = await fetch(url, { signal: searchAbort.signal });
      if (!response.ok) throw new Error('search');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = $('[data-predictive]', doc);
      searchResults.innerHTML = source ? source.innerHTML : '';
      $$('.oq-search__item', searchResults).forEach((item, i) => item.style.setProperty('--i', i));
    } catch (error) { if (error.name !== 'AbortError') searchResults.replaceChildren(); }
  };
  searchInput?.addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => runSearch(searchInput.value.trim()), 220); });

  /* ---------- 7. Promotion Code Copy ----------------------------------- */
  document.addEventListener('click', (e) => {
    const copyBtn = e.target.closest('[data-copy-code]');
    if (!copyBtn) return;
    const code = copyBtn.getAttribute('data-copy-code') || 'ORBELLIQ15';
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
    const origText = copyBtn.innerHTML;
    copyBtn.classList.add('is-copied');
    copyBtn.textContent = `✓ COPIED: ${code}`;
    showToast(`Code ${code} copied. Apply it at checkout if currently active.`);
    window.setTimeout(() => {
      copyBtn.classList.remove('is-copied');
      copyBtn.innerHTML = origText;
    }, 2400);
  });

  /* ---------- 8. Shopify Markets + Native Shopify Cart ----------------- */
  const DRAWER_SECTION = config.drawerSection || 'orbelliq-cart-drawer';
  const drawer = $('[data-cart-drawer]');
  const drawerPanel = $('.oq-drawer__panel', drawer || document);
  let drawerReturn = null;
  const onCartTemplate = document.body.classList.contains('template-cart');

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      const prior = el.textContent;
      el.textContent = String(count);
      el.dataset.count = String(count);
      if (String(prior) !== String(count) && !reduced) {
        el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
      }
    });
    $$('[data-cart-link]').forEach((el) => el.setAttribute('aria-label', `${text.bag || 'Bag'}, ${count} ${text.items || 'items'}`));
  };

  const setDrawer = (open) => {
    if (!drawer) return;
    const wasOpen = drawer.classList.contains('is-open');
    if (wasOpen === open) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    if (open) {
      drawerReturn = document.activeElement;
      root.classList.remove('oq-header-hidden');
      lockScroll();
      window.setTimeout(() => drawerPanel?.focus(), 80);
    } else {
      unlockScroll();
      drawerReturn?.focus?.();
    }
  };

  const paintDrawer = (html) => {
    if (!html || !drawer) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = $('[data-cart-body]', doc);
    const current = $('[data-cart-body]', drawer);
    if (!next || !current) return;
    current.innerHTML = next.innerHTML;
    current.dataset.count = next.dataset.count || '0';
    $$('.oq-line', current).forEach((line, i) => line.style.setProperty('--i', i));
    updateCount(Number(next.dataset.count || 0));
  };

  const refreshDrawer = async () => {
    try {
      const url = new URL(rootUrl, window.location.origin);
      url.searchParams.set('sections', DRAWER_SECTION);
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to refresh your bag.');
      const data = await response.json();
      paintDrawer(data[DRAWER_SECTION]);
    } catch (error) {
      showToast(error.message || text.error || 'Unable to refresh your bag.');
    }
  };

  const cartRequest = async (url, options) => {
    const response = await fetch(url, options);
    let data = {};
    try { data = await response.json(); } catch (_) { /* non-JSON response */ }
    if (!response.ok) throw new Error(data.description || data.message || text.error || 'Something went wrong. Please try again.');
    return data;
  };

  const setButtonBusy = (button, busy) => {
    if (!button) return;
    if (busy) {
      button.dataset.originalLabel = button.innerHTML;
      button.classList.add('is-loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (!button.dataset.keepDisabled) button.disabled = false;
      if (button.dataset.originalLabel) button.innerHTML = button.dataset.originalLabel;
    }
  };

  const addProductForm = async (form, button) => {
    setButtonBusy(button, true);
    try {
      const body = new FormData(form);
      body.append('sections', DRAWER_SECTION);
      body.append('sections_url', window.location.pathname + window.location.search);
      const data = await cartRequest(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body
      });
      if (data.sections?.[DRAWER_SECTION]) paintDrawer(data.sections[DRAWER_SECTION]);
      else await refreshDrawer();
      setDrawer(true);
      showToast(text.added || 'Added to your bag');
    } catch (error) {
      showToast(error.message);
    } finally {
      window.setTimeout(() => setButtonBusy(button, false), 500);
    }
  };

  const addVariantToCart = async (variantId, quantity = 1, button = null, productUrl = '') => {
    if (!variantId) {
      if (productUrl) window.location.assign(productUrl);
      else showToast('Please open the product page and choose an available variant.');
      return;
    }
    setButtonBusy(button, true);
    try {
      const body = new FormData();
      body.append('id', String(variantId));
      body.append('quantity', String(Math.max(1, Number(quantity) || 1)));
      body.append('sections', DRAWER_SECTION);
      body.append('sections_url', window.location.pathname + window.location.search);
      const data = await cartRequest(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body
      });
      if (data.sections?.[DRAWER_SECTION]) paintDrawer(data.sections[DRAWER_SECTION]);
      else await refreshDrawer();
      setDrawer(true);
      showToast(text.added || 'Added to your bag');
    } catch (error) {
      showToast(error.message);
    } finally {
      window.setTimeout(() => setButtonBusy(button, false), 500);
    }
  };

  const changeLine = async (lineEl, quantity) => {
    const line = Number(lineEl?.dataset.line);
    if (!line) return;
    lineEl.classList.add('is-busy');
    try {
      const data = await cartRequest(`${routes.cartChange || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: Math.max(0, Number(quantity) || 0), sections: DRAWER_SECTION, sections_url: window.location.pathname + window.location.search })
      });
      if (data.sections?.[DRAWER_SECTION]) paintDrawer(data.sections[DRAWER_SECTION]);
      else await refreshDrawer();
      if (onCartTemplate) window.location.reload();
    } catch (error) {
      lineEl.classList.remove('is-busy');
      showToast(error.message);
    }
  };

  drawer?.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-close]')) { setDrawer(false); return; }
    const lineEl = event.target.closest('.oq-line');
    if (!lineEl) return;
    const current = Number(lineEl.dataset.qty || 1);
    if (event.target.closest('[data-qty-minus]')) changeLine(lineEl, Math.max(0, current - 1));
    else if (event.target.closest('[data-qty-plus]')) changeLine(lineEl, current + 1);
    else if (event.target.closest('[data-line-remove]')) changeLine(lineEl, 0);
  });
  drawer?.addEventListener('change', (event) => {
    const input = event.target.closest('.oq-line input[type="number"]');
    if (input) changeLine(input.closest('.oq-line'), Math.max(0, parseInt(input.value, 10) || 0));
  });
  drawer?.addEventListener('keydown', (event) => trapTab(event, drawerPanel));

  $$('[data-cart-open]').forEach((link) => link.addEventListener('click', (event) => {
    if (!drawer || onCartTemplate || event.metaKey || event.ctrlKey || event.shiftKey || event.button) return;
    event.preventDefault();
    setDrawer(true);
  }));

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-product-form],[data-quick-add]');
    if (!form || !drawer) return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button?.disabled) return;
    event.preventDefault();
    addProductForm(form, button);
  });

  document.addEventListener('click', (event) => {
    const quickButton = event.target.closest('[data-oq-quick-add]');
    if (!quickButton || quickButton.disabled || quickButton.type === 'submit') return;
    event.preventDefault();
    addVariantToCart(quickButton.dataset.variantId, quickButton.dataset.quantity || 1, quickButton, quickButton.dataset.productUrl || '');
  });

  /* ---------- 9. Generic Quantity Steppers ------------------------------ */
  let cartPageTimer;
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-step]');
    if (!step || step.closest('.oq-drawer')) return;
    const wrap = step.closest('.oq-qty');
    const input = $('input', wrap);
    if (!input) return;
    const min = Number(input.min || 1);
    const next = Math.max(min, (parseInt(input.value, 10) || 1) + Number(step.dataset.step));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const cartForm = input.closest('[data-cart-form]');
    if (cartForm) { clearTimeout(cartPageTimer); cartPageTimer = setTimeout(() => cartForm.requestSubmit($('[name="update"]', cartForm)), 650); }
  });

  /* ---------- 10. Product Variant, Availability & Sticky ATC ------------ */
  const renderVariantPrice = (container, priceText, compareText, savingsPct) => {
    if (!container) return;
    const wrap = document.createElement('span');
    wrap.className = 'orbelliq-price';
    const current = document.createElement('span');
    current.className = 'orbelliq-price__current';
    current.textContent = priceText || '';
    wrap.append(current);
    if (compareText) {
      const compare = document.createElement('s');
      compare.className = 'orbelliq-price__compare';
      compare.textContent = compareText;
      wrap.append(compare);
      if (savingsPct) {
        const badge = document.createElement('span');
        badge.className = 'orbelliq-price__badge';
        badge.textContent = `SAVE ${savingsPct}%`;
        wrap.append(badge);
      }
    }
    container.replaceChildren(wrap);
  };

  const initProduct = (scope = document) => {
    $$('[data-product-root]', scope).forEach((rootEl) => {
      if (rootEl.dataset.ready) return;
      rootEl.dataset.ready = '1';
      const form = $('[data-product-form]', rootEl);
      const variantField = $('[data-variant-id]', form || rootEl);
      const mainButton = $('[data-main-atc]', form || rootEl);
      const price = $('[data-product-price]', rootEl);
      const stock = $('[data-product-stock]', rootEl);
      const availability = $('#orbelliq-pdp-conversion-box [data-product-availability]');
      const sticky = $('#orbelliq-sticky-atc');
      const stickyButton = $('[data-sticky-button]', sticky || document);
      const stickyPrice = $('[data-sticky-price]', sticky || document);
      const stickyVariant = $('[data-sticky-variant-select]', sticky || document);
      const pills = $$('[data-option-pill]', rootEl);
      const footer = $('.site-footer');

      const syncVariant = () => {
        if (!variantField) return;
        const option = variantField.tagName === 'SELECT' ? variantField.options[variantField.selectedIndex] : variantField;
        if (!option) return;
        const available = option.dataset.available === 'true';
        const priceText = option.dataset.price || '';
        const compareText = option.dataset.compareAt || '';
        renderVariantPrice(price, priceText, compareText, option.dataset.savings);
        renderVariantPrice(stickyPrice, priceText, compareText, option.dataset.savings);
        if (mainButton) {
          mainButton.disabled = !available;
          mainButton.textContent = available ? 'ADD TO BAG' : 'SOLD OUT';
          if (available) delete mainButton.dataset.keepDisabled; else mainButton.dataset.keepDisabled = 'true';
        }
        if (stickyButton) {
          stickyButton.disabled = !available;
          stickyButton.textContent = available ? 'ADD TO BAG — EXPRESS' : 'SOLD OUT';
          if (available) delete stickyButton.dataset.keepDisabled; else stickyButton.dataset.keepDisabled = 'true';
        }
        if (stock) {
          stock.textContent = available ? 'Available to order' : 'This variant is currently sold out';
          stock.classList.toggle('is-available', available);
        }
        if (availability) {
          availability.textContent = available ? 'Available to order' : 'Currently sold out';
          availability.classList.toggle('is-available', available);
        }
        if (stickyVariant && stickyVariant.value !== variantField.value) stickyVariant.value = variantField.value;
        pills.forEach((pill) => pill.setAttribute('aria-pressed', String(pill.dataset.pillVariant === String(variantField.value))));
        if (variantField.tagName === 'SELECT') {
          const selectedMedia = option.dataset.media;
          if (selectedMedia) {
            const mediaEl = $(`[data-media-id="${CSS.escape(selectedMedia)}"]`, rootEl);
            mediaEl?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
          }
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantField.value);
        window.history.replaceState({}, '', url);
      };

      pills.forEach((pill) => pill.addEventListener('click', () => {
        if (!variantField || pill.disabled) return;
        variantField.value = pill.dataset.pillVariant;
        variantField.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      variantField?.addEventListener('change', syncVariant);
      stickyVariant?.addEventListener('change', () => {
        if (!variantField) return;
        variantField.value = stickyVariant.value;
        variantField.dispatchEvent(new Event('change', { bubbles: true }));
      });
      stickyButton?.addEventListener('click', () => {
        if (!form || !mainButton || mainButton.disabled) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
        else mainButton.click();
      });
      syncVariant();

      if (sticky) {
        const syncSticky = () => {
          const buyRect = (mainButton || form || rootEl).getBoundingClientRect();
          const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
          const visible = buyRect.bottom < 0 && !footerVisible;
          sticky.classList.toggle('is-visible', visible);
          sticky.setAttribute('aria-hidden', String(!visible));
          document.body.classList.toggle('has-sticky-atc', visible);
        };
        window.addEventListener('scroll', syncSticky, { passive: true });
        window.addEventListener('resize', syncSticky, { passive: true });
        syncSticky();
      }
    });
  };

  /* ---------- 12. Product Gallery Lightbox ------------------------------ */
  let lightbox;
  let lightboxItems = [];
  let lightboxIndex = 0;
  let lightboxReturn = null;
  const paintLightbox = () => {
    const img = $('img', lightbox);
    const item = lightboxItems[lightboxIndex];
    if (img && item) { img.src = item.dataset.full; img.alt = item.dataset.alt || ''; }
    $('.p', lightbox).hidden = $('.n', lightbox).hidden = lightboxItems.length < 2;
  };
  function closeLightbox() {
    if (!lightbox || !lightbox.classList.contains('is-open')) return;
    lightbox.classList.remove('is-open');
    unlockScroll();
    lightboxReturn?.focus?.();
  }
  const openLightbox = (item) => {
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'oq-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', text.gallery || 'Product gallery');
      lightbox.innerHTML = '<button class="x" type="button" aria-label="Close">×</button><button class="p" type="button" aria-label="Previous">←</button><img alt=""><button class="n" type="button" aria-label="Next">→</button>';
      document.body.append(lightbox);
      lightbox.addEventListener('click', (event) => {
        if (event.target.closest('.x') || event.target === lightbox) closeLightbox();
        else if (event.target.closest('.p')) { lightboxIndex = (lightboxIndex - 1 + lightboxItems.length) % lightboxItems.length; paintLightbox(); }
        else if (event.target.closest('.n')) { lightboxIndex = (lightboxIndex + 1) % lightboxItems.length; paintLightbox(); }
      });
      document.addEventListener('keydown', (event) => {
        if (!lightbox.classList.contains('is-open')) return;
        if (event.key === 'ArrowLeft') $('.p', lightbox).click();
        if (event.key === 'ArrowRight') $('.n', lightbox).click();
      });
    }
    lightboxItems = $$('[data-lightbox]');
    lightboxIndex = Math.max(0, lightboxItems.indexOf(item));
    lightboxReturn = item;
    paintLightbox();
    lightbox.classList.add('is-open');
    lockScroll();
    $('.x', lightbox).focus();
  };
  document.addEventListener('click', (event) => {
    const item = event.target.closest('[data-lightbox]');
    if (item) openLightbox(item);
  });

  /* ---------- 13. Interactive Compression Illustration ------------------ */
  const simInput = $('[data-oq-sim-input]');
  if (simInput) {
    const pctEl = $('[data-oq-sim-pct]');
    const volEl = $('[data-oq-sim-volume]');
    const daysEl = $('[data-oq-sim-days]');
    const feesEl = $('[data-oq-sim-fees]');
    const stackEl = $('[data-oq-sim-stack]');
    const heightEl = $('[data-oq-sim-height]');
    const statusEl = $('[data-oq-sim-status]');

    const updateSim = () => {
      const val = Number(simInput.value);
      const gapPx = Math.max(3, Math.round(18 - (val / 100) * 14));
      const padPx = Math.max(7, Math.round(18 - (val / 100) * 10));

      if (pctEl) pctEl.textContent = `ILLUSTRATIVE MODEL: ${Math.round(val)}%`;
      if (volEl) volEl.textContent = `STEP ${Math.round(val)}/100`;
      if (daysEl) daysEl.textContent = 'Depends on packed items';
      if (feesEl) feesEl.textContent = 'Not estimated';
      if (heightEl) heightEl.textContent = 'MODEL ONLY';
      if (stackEl) {
        stackEl.style.setProperty('--oq-stack-gap', `${gapPx}px`);
        stackEl.style.setProperty('--oq-layer-pad', `${padPx}px`);
      }
      if (statusEl) statusEl.textContent = '● ILLUSTRATIVE MODEL VIEW';
    };
    simInput.addEventListener('input', updateSim);
    updateSim();
  }

  /* ---------- 13. Bundle selection helper ------------------------------ */
  function updateBundleCalculation() {
    const builder = $('[data-oq-bundle-builder]');
    if (!builder) return;
    const names = $$('.oq-bundle-option.is-selected', builder).map((button) => button.dataset.title).filter(Boolean);
    const summary = $('[data-oq-bundle-summary-text]', builder);
    if (summary) summary.textContent = names.length ? names.join(' + ') : 'Choose one item from each category';
  }
  $$('[data-oq-bundle-pick]').forEach((button) => {
    button.addEventListener('click', () => {
      const slot = button.dataset.oqBundlePick;
      const column = button.closest(`[data-oq-slot-col="${slot}"]`);
      $$('.oq-bundle-option', column || document).forEach((sibling) => sibling.classList.toggle('is-selected', sibling === button));
      updateBundleCalculation();
    });
  });
  updateBundleCalculation();

  /* ---------- 15. Client-Side Category Pill & Keyword Filter ------------ */
  $$('[data-oq-filterable-section]').forEach((section) => {
    const grid = $('[data-oq-filter-grid]', section);
    if (!grid) return;
    const cards = $$('[data-oq-card]', grid);
    const filterBtns = $$('[data-oq-filter-btn]', section);
    const keywordInput = $('[data-oq-keyword-filter]', section);
    let activeCat = 'all';
    let activeQuery = '';

    const applyFilter = () => {
      cards.forEach((card) => {
        const cat = card.getAttribute('data-oq-category') || 'all';
        const cardText = (card.textContent || '').toLowerCase();
        const matchCat = activeCat === 'all' || cat === activeCat;
        const matchQuery = !activeQuery || cardText.includes(activeQuery);
        card.classList.toggle('is-hidden-by-filter', !(matchCat && matchQuery));
      });
    };

    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        filterBtns.forEach((b) => b.classList.remove('is-current'));
        btn.classList.add('is-current');
        activeCat = btn.getAttribute('data-oq-filter-btn') || 'all';
        applyFilter();
      });
    });

    keywordInput?.addEventListener('input', () => {
      activeQuery = keywordInput.value.trim().toLowerCase();
      applyFilter();
    });
  });

  /* ---------- 15. Quick View & Product-Finder Quiz --------------------- */
  const qvModal = $('#oq-quickview-modal');
  const qvTitle = $('[data-oq-qv-title]', qvModal || document);
  const qvPrice = $('[data-oq-qv-price]', qvModal || document);
  const qvDesc = $('[data-oq-qv-desc]', qvModal || document);
  const qvAdd = $('[data-oq-qv-add]', qvModal || document);
  const qvLink = $('[data-oq-qv-link]', qvModal || document);

  document.addEventListener('click', (event) => {
    const quickView = event.target.closest('[data-oq-quick-view]');
    if (quickView && qvModal) {
      event.preventDefault();
      const title = quickView.dataset.productTitle || 'ORBELLIQ system';
      const url = quickView.dataset.productUrl || '/collections/all';
      if (qvTitle) qvTitle.textContent = title;
      if (qvPrice) qvPrice.textContent = quickView.dataset.productPrice || 'See live price on product page';
      if (qvDesc) qvDesc.textContent = quickView.dataset.productDesc || 'View the product page for full details and current availability.';
      if (qvLink) qvLink.href = url;
      if (qvAdd) {
        qvAdd.dataset.variantId = quickView.dataset.variantId || '';
        qvAdd.dataset.productUrl = url;
        qvAdd.dataset.productTitle = title;
        qvAdd.disabled = quickView.disabled;
      }
      qvModal.hidden = false;
      qvModal.setAttribute('aria-hidden', 'false');
    }
    if (event.target.closest('[data-oq-modal-close]')) {
      $$('.oq-modal').forEach((modal) => { modal.hidden = true; modal.setAttribute('aria-hidden', 'true'); });
    }
  });

  qvAdd?.addEventListener('click', () => {
    if (qvModal) { qvModal.hidden = true; qvModal.setAttribute('aria-hidden', 'true'); }
    addVariantToCart(qvAdd.dataset.variantId, 1, qvAdd, qvAdd.dataset.productUrl || '');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (qvModal && !qvModal.hidden) { qvModal.hidden = true; qvModal.setAttribute('aria-hidden', 'true'); }
    else if (searchOpen) setSearch(false);
    else if (drawer?.classList.contains('is-open')) setDrawer(false);
    else if (menuOpen) setMenu(false);
    else closeLightbox();
  });

  const quizRecs = {
    cabin: { title: 'Cabin and compression travel', desc: 'Explore carry-on luggage, compression bags and packing systems for more organized travel.', query: 'carry-on luggage and compression bags' },
    suit: { title: 'Garment and business travel', desc: 'Explore garment carriers, weekender bags and organizers designed for business trips.', query: 'garment carriers and weekender bags' },
    tech: { title: 'Travel technology and power', desc: 'Explore charging accessories, tech organizers and travel power products.', query: 'travel tech and charging accessories' },
    sanctuary: { title: 'Home and personal comfort', desc: 'Explore home comfort, grooming and personal-care accessories.', query: 'home and personal comfort accessories' }
  };
  $$('[data-oq-quiz-pick]').forEach((button) => {
    button.addEventListener('click', () => {
      const group = button.dataset.oqQuizPick;
      const card = button.closest(`[data-oq-quiz-group="${group}"]`);
      $$('.oq-quiz-choice', card || document).forEach((choice) => choice.classList.toggle('is-selected', choice === button));
      const selected = $('[data-oq-quiz-group="1"] .oq-quiz-choice.is-selected')?.dataset.val || 'cabin';
      const recommendation = quizRecs[selected] || quizRecs.cabin;
      const title = $('[data-oq-quiz-rec-title]');
      const description = $('[data-oq-quiz-rec-desc]');
      const link = $('[data-oq-quiz-link]');
      if (title) title.textContent = recommendation.title;
      if (description) description.textContent = recommendation.desc;
      if (link) {
        link.href = `${routes.search || '/search'}?q=${encodeURIComponent(recommendation.query)}`;
        link.textContent = 'EXPLORE MATCHING PRODUCTS →';
      }
    });
  });

  /* ---------- 18. Scroll-Reveal IntersectionObserver (All Sections) ----- */
  const REVEAL = [
    '.section-heading', '.oq-page-header__inner > *', '.oq-pillar', '.system-category-card',
    '.product-card', '.orbelliq-manifesto__inner > *', '.oq-news', '.article-card',
    '.oq-collection-card', '.cart-line', '.spec-table-wrap', '.product-info > *',
    '.product-gallery > *', '.oq-card', '.oq-auth > *', '.oq-contact__aside',
    '.oq-contact__form', '.featured-collection__action', '.collection-filter-pills',
    '.footer-grid > *', '.oq-404__inner > *', '.oq-article > *', '.oq-account > *',
    '.oq-address-grid > *', '.oq-lab-grid > *', '.oq-bundle-column', '.oq-bundle-dock',
    '.oq-quiz-shell', '.oq-review-card', '.oq-faq-item', '.oq-tracker-panel'
  ];
  let revealObserver;
  const initReveal = (scope = document) => {
    if (reduced) return;
    if (!('IntersectionObserver' in window)) { $$('[data-reveal]', scope).forEach((el) => el.classList.add('is-in')); return; }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    REVEAL.forEach((selector) => {
      $$(selector, scope).forEach((el) => {
        if (el.dataset.revealDone) return;
        el.dataset.revealDone = '1';
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', el.matches('.oq-news,.spec-table-wrap,.oq-quiz-shell') ? 'scale' : '');
        const siblingIndex = Array.from(el.parentElement?.children || []).indexOf(el);
        el.style.setProperty('--d', `${Math.min(siblingIndex, 7) * 65}ms`);
        revealObserver.observe(el);
      });
    });
    $$('[data-reveal]:not([data-reveal-done])', scope).forEach((el) => revealObserver.observe(el));
  };
  window.setTimeout(() => {
    $$('[data-reveal]:not(.is-in)').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in'); });
  }, 3200);

  /* ---------- 19. Unified 3D Magnetic Pointer Physics ------------------- */
  if (!reduced && finePointer) {
    let frame = 0;
    document.addEventListener('pointermove', (event) => {
      if (frame) return;
      const { clientX, clientY, target } = event;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const card = target.closest?.('.product-card, .system-category-card, .oq-pillar, .oq-collection-card');
        if (card) {
          const rect = card.getBoundingClientRect();
          const x = (clientX - rect.left) / rect.width;
          const y = (clientY - rect.top) / rect.height;
          card.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
          card.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
          if (!card.matches('.oq-pillar, .oq-collection-card')) {
            card.style.setProperty('--tx', `${((0.5 - y) * 4).toFixed(2)}deg`);
            card.style.setProperty('--ty', `${((x - 0.5) * 4).toFixed(2)}deg`);
          }
        }
        const hero = target.closest?.('.hero');
        if (hero) {
          const rect = hero.getBoundingClientRect();
          hero.style.setProperty('--hx', `${(((clientX - rect.left) / rect.width) * 100).toFixed(1)}%`);
          hero.style.setProperty('--hy', `${(((clientY - rect.top) / rect.height) * 100).toFixed(1)}%`);
        }
        const button = target.closest?.('.hero-actions .button, .oq-news .button, .button--magnetic');
        if (button) {
          const rect = button.getBoundingClientRect();
          button.style.setProperty('--mx', `${clamp((clientX - (rect.left + rect.width / 2)) * 0.12, -7, 7).toFixed(1)}px`);
          button.style.setProperty('--my', `${clamp((clientY - (rect.top + rect.height / 2)) * 0.2, -5, 5).toFixed(1)}px`);
        }
      });
    }, { passive: true });
    document.addEventListener('pointerout', (event) => {
      const card = event.target.closest?.('.product-card, .system-category-card');
      if (card && !card.contains(event.relatedTarget)) { card.style.setProperty('--tx', '0deg'); card.style.setProperty('--ty', '0deg'); }
      const button = event.target.closest?.('.button');
      if (button && !button.contains(event.relatedTarget)) { button.style.setProperty('--mx', '0px'); button.style.setProperty('--my', '0px'); }
    }, { passive: true });
  }


  /* ---------- 21. v5.0 APEX: Anatomy Hotspots, Airline Guide, Compare & Offers --- */
  const hotspotData = {
    '1': { code: 'CATEGORY 01 // COMPRESSION & CLOSURE', title: 'Compression and closure components', desc: 'Explore products with compression or closure features. The exact mechanism, performance and included accessories vary by item; check the live product listing for confirmed details.', m1: 'Compression and closure', m2: 'See the product listing', m3: 'Travel bags and organizers', prodTitle: 'travel compression bags and backpacks' },
    '2': { code: 'CATEGORY 02 // BAG CONSTRUCTION', title: 'Bag and shell construction', desc: 'Browse bags and travel organizers. Confirm dimensions, fabrics, capacity and care instructions on the individual product page before ordering.', m1: 'Bag construction', m2: 'Materials vary by item', m3: 'Travel bags and organizers', prodTitle: 'travel bags and luggage' },
    '3': { code: 'CATEGORY 03 // ZIPS & CLOSURES', title: 'Zips, closures and access', desc: 'Explore products with different access and closure layouts. Check each listing for the exact hardware, configuration and included accessories.', m1: 'Access and closure', m2: 'Details vary by item', m3: 'Packing organizers', prodTitle: 'packing cubes and travel organizers' },
    '4': { code: 'CATEGORY 04 // TRAVEL POWER', title: 'Travel power accessories', desc: 'Browse charging and travel-power accessories. Confirm wattage, port types, supported regions and device compatibility on the product page.', m1: 'Charging accessories', m2: 'Check compatibility', m3: 'Travel technology', prodTitle: 'travel power adapters and chargers' },
    '5': { code: 'CATEGORY 05 // TECH ORGANIZATION', title: 'Tech and cable organization', desc: 'Explore organizers for cables and everyday technology. Layout, dimensions and included accessories are product-specific.', m1: 'Tech organization', m2: 'See exact contents', m3: 'Tech pouches and cases', prodTitle: 'tech pouches and cable organizers' },
    '6': { code: 'CATEGORY 06 // WET & DRY ORGANIZATION', title: 'Wet and dry organization', desc: 'Browse toiletry and personal-care organizers. Review the product listing for materials, sealing details and care instructions; water resistance is not implied.', m1: 'Personal-care storage', m2: 'Check material details', m3: 'Toiletry organizers', prodTitle: 'toiletry bags and organizers' }
  };

  $$('[data-oq-hotspot]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-oq-hotspot');
      const d = hotspotData[id];
      if (!d) return;
      $$('[data-oq-hotspot]').forEach((b) => b.classList.toggle('is-active', b.getAttribute('data-oq-hotspot') === id));
      const codeEl = $('[data-oq-anat-code]');
      const titleEl = $('[data-oq-anat-title]');
      const descEl = $('[data-oq-anat-desc]');
      const m1El = $('[data-oq-anat-m1]');
      const m2El = $('[data-oq-anat-m2]');
      const m3El = $('[data-oq-anat-m3]');
      const ctaEl = $('[data-oq-anat-cta]');
      if (codeEl) codeEl.textContent = d.code;
      if (titleEl) titleEl.textContent = d.title;
      if (descEl) descEl.textContent = d.desc;
      if (m1El) m1El.textContent = d.m1;
      if (m2El) m2El.textContent = d.m2;
      if (m3El) m3El.textContent = d.m3;
      if (ctaEl) {
        ctaEl.href = `${routes.search || '/search'}?q=${encodeURIComponent(d.prodTitle)}`;
        ctaEl.textContent = 'FIND THIS PRODUCT IN THE CATALOG →';
      }
    });
  });

  /* Airline Cabin Compliance & Annual Baggage Fee Calculator */
  const airlineSelect = $('[data-oq-airline-select]');
  const tripsInput = $('[data-oq-trips-input]');
  const updateAirlineCalc = () => {
    if (!airlineSelect || !tripsInput) return;
    const opt = airlineSelect.options[airlineSelect.selectedIndex];
    const trips = Number(tripsInput.value || 6);
    const feeInr = Number(opt?.getAttribute('data-fee-inr') || 0) * trips;
    const dim = opt?.getAttribute('data-dim') || '';

    const tripsLabel = $('[data-oq-trips-label]');
    const dimEl = $('[data-oq-airline-dim]');
    const savEl = $('[data-oq-airline-savings]');
    if (tripsLabel) tripsLabel.textContent = `${trips} Round-Trip${trips > 1 ? 's' : ''} / Year`;
    if (dimEl) dimEl.textContent = dim;
    if (savEl) savEl.textContent = feeInr > 0 ? `Illustrative fee reference: ₹${feeInr.toLocaleString('en-IN')} / year` : 'Check the carrier’s current baggage fees';
  };
  airlineSelect?.addEventListener('change', updateAirlineCalc);
  tripsInput?.addEventListener('input', updateAirlineCalc);
  updateAirlineCalc();

  /* Side-by-Side Product Comparison */
  let compareList = [];
  const compareDock = $('#oq-compare-dock');
  const compareCountEl = $('[data-oq-compare-count]');
  const compareModal = $('#oq-compare-modal');
  const compareTableWrap = $('[data-oq-compare-table-wrap]');
  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  const syncCompareUI = () => {
    if (!compareDock) return;
    compareDock.hidden = compareList.length === 0;
    if (compareCountEl) compareCountEl.textContent = `${compareList.length} product${compareList.length === 1 ? '' : 's'} selected`;
    $$('[data-oq-compare-add]').forEach((button) => {
      button.classList.toggle('is-selected', compareList.some((item) => item.title === button.dataset.productTitle));
    });
  };

  document.addEventListener('click', (event) => {
    const compareButton = event.target.closest('[data-oq-compare-add]');
    if (compareButton) {
      event.preventDefault();
      const product = {
        title: compareButton.dataset.productTitle || 'Product',
        url: compareButton.dataset.productUrl || '/collections/all',
        category: compareButton.dataset.cat || 'Product'
      };
      const index = compareList.findIndex((item) => item.title === product.title);
      if (index >= 0) compareList.splice(index, 1);
      else {
        if (compareList.length >= 3) compareList.shift();
        compareList.push(product);
        showToast(`Added ${product.title} to comparison`);
      }
      syncCompareUI();
    }

    if (event.target.closest('[data-oq-compare-clear]')) {
      compareList = [];
      syncCompareUI();
    }

    if (event.target.closest('[data-oq-compare-open]') && compareModal && compareTableWrap) {
      if (!compareList.length) return;
      compareTableWrap.innerHTML = `
        <table class="spec-table">
          <thead><tr><th>Product</th>${compareList.map((item) => `<th>${escapeHTML(item.title)}</th>`).join('')}</tr></thead>
          <tbody>
            <tr><th>Category</th>${compareList.map((item) => `<td>${escapeHTML(item.category)}</td>`).join('')}</tr>
            <tr><th>Product details</th>${compareList.map((item) => `<td><a href="${escapeHTML(item.url)}">View product details and specifications →</a></td>`).join('')}</tr>
            <tr><th>Price and availability</th>${compareList.map((item) => `<td>See the live product page for current variant pricing and availability.</td>`).join('')}</tr>
          </tbody>
        </table>`;
      compareModal.hidden = false;
      compareModal.setAttribute('aria-hidden', 'false');
    }

    if (event.target.closest('[data-oq-vault-open]')) {
      const vaultModal = $('#oq-vault-modal');
      if (vaultModal) { vaultModal.hidden = false; vaultModal.setAttribute('aria-hidden', 'false'); }
    }
    if (event.target.closest('[data-oq-vault-spin]')) {
      const status = $('[data-oq-vault-status]');
      if (!status) return;
      status.textContent = 'No reward is unlocked here. Check the current offers shown on the product page and at checkout.';
      status.style.color = '';
    }

    const charterTab = event.target.closest('[data-oq-charter-tab]');
    if (charterTab) {
      const target = charterTab.getAttribute('data-oq-charter-tab');
      const wrap = charterTab.closest('[data-oq-charter]');
      if (wrap) {
        $$('[data-oq-charter-tab]', wrap).forEach((tab) => tab.classList.toggle('is-active', tab === charterTab));
        $$('[data-oq-charter-pane]', wrap).forEach((pane) => { pane.hidden = pane.getAttribute('data-oq-charter-pane') !== target; });
      }
    }
  });

  // Activate charter tab automatically if URL hash matches #oq-charter-*
  const checkCharterHash = () => {
    const h = window.location.hash;
    if (h && h.startsWith('#oq-charter-')) {
      const key = h.replace('#oq-charter-', '');
      const btn = $(`[data-oq-charter-tab="${key}"]`);
      if (btn) {
        btn.click();
        btn.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      }
    }
  };
  window.addEventListener('hashchange', checkCharterHash);
  window.setTimeout(checkCharterHash, 300);

  /* ---------- 20. Boot & Initialize ------------------------------------- */
  const init = (scope = document) => {
    initProduct(scope);
    initReveal(scope);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
